/**
 * src/features/pro-module/data/negotiationGames.js
 * Logic-tree for the Module 2 Negotiation Simulator.
 * Updated with ADAPTIVE DIFFICULTY (The Opponent Matrix).
 */

export const NEGOTIATION_GAMES = {
  'first-offer-negotiation': {
    id: 'game_negotiation_1',
    moduleId: 'negotiation',
    title: 'The $500k Conversation',
    
    targetSalary: 60000,
    
    // --- DYNAMIC DIFFICULTY MATRIX ---
    // Maps Confidence Slider (0-10) to AI Persona & Starting Offer
    difficultyLevels: {
        rookie: {
            label: "TRAINING (Friendly)",
            opponentType: 'nervous_recruiter', // Easy to sway
            offerGap: 2000, // Starts at $58k (Target $60k)
            turnLimit: 8,
            initialMessage: "Hey! We really want you. We can do $58,000 to start?"
        },
        standard: {
            label: "STANDARD (The Gatekeeper)",
            opponentType: 'standard_hr', // Uses "Policy" as a shield
            offerGap: 5000, // Starts at $55k
            turnLimit: 6,
            initialMessage: "Mark, we're thrilled to offer you the role. The base is $55,000."
        },
        expert: {
            label: "EXPERT (The Shark)",
            opponentType: 'aggressive_founder', // Cocky, impatient
            offerGap: 12000, // Starts at $48k (Huge Lowball)
            turnLimit: 4, // Impatient - fewer turns to close
            initialMessage: "Look, we like you, but budgets are tight. $48k is the number. Take it or leave it."
        }
    },
    
    // 1. THE BRIEFING
    preGameBriefing: {
        title: "The First Number is a Test",
        script: "The most expensive mistake you will ever make is saying 'Yes' too fast. The first offer is rarely the best offer. It’s a test of your business acumen. The Mission: HR just dropped a number. It’s lower than you wanted. Negotiate a better deal without being a jerk.",
        videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
    },

    // 2. FAILURE LOGIC
    failureLogic: {
        'accepted_too_fast': {
            title: "Left Money on the Table",
            script: "Stop. You accepted the very first number. In business, that signals desperation or lack of market awareness. Even if the offer is good, you must pause. 'I appreciate the offer, let me review the details.' Never say 'Yes' on the phone.",
            videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
        },
        'aggressive': {
            title: "Rapport Broken",
            script: "You got aggressive. Negotiation isn't a fistfight; it's a collaboration. If you make them feel like an enemy, they might withdraw the offer entirely. Use the 'Collaborative Friction' tone.",
            videoUrl: null
        },
        'stalled': {
            title: "Negotiation Stalled",
            script: "You went in circles. You didn't give them a reason to move. You need to provide 'New Information' (Market Data, Competitor Offers, Unique Skills) to justify a higher number.",
            videoUrl: null
        }
    }
  },

  // --- NEW: THE INTERNAL PROMOTION ---
  'internal-promotion': {
    id: 'game_negotiation_internal',
    moduleId: 'negotiation',
    title: 'The "No Budget" Promotion',
    targetSalary: 85000,
    difficultyLevels: {
        rookie: {
            label: "TRAINING (Hesitant Manager)",
            opponentType: 'standard_hr', 
            offerGap: 10000, 
            turnLimit: 8,
            initialMessage: "You've done great work taking on the Senior Analyst duties. We're officially promoting you! However, corporate only approved a 4% raise ($75k). Budgets are frozen."
        },
        standard: {
            label: "STANDARD (The Corporate Wall)",
            opponentType: 'standard_hr', 
            offerGap: 15000, 
            turnLimit: 6,
            initialMessage: "You've been doing the Senior role already. We can make the title official, but budgets are frozen, so base pay stays at $70k for now."
        },
        expert: {
            label: "EXPERT (The Gaslighter)",
            opponentType: 'aggressive_founder', 
            offerGap: 20000, 
            turnLimit: 4,
            initialMessage: "Here's the new Senior title. No raise attached right now, but it's a great opportunity for exposure. Sound good?"
        }
    },
    preGameBriefing: {
        title: "Pivoting the Ask",
        script: "Your boss just gave you a title promotion and way more work, but blamed 'Corporate' for a tiny raise. The Trap: If they truly have no cash budget, arguing for cash will fail. The Mission: Pivot the negotiation. If they can't give cash, negotiate for Equity, extra PTO, a guaranteed 6-month review, or a signing bonus.",
        videoUrl: null
    },
    failureLogic: {
        'accepted_too_fast': { title: "Free Labor", script: "You just accepted a massive increase in workload for free. You devalued your own labor." },
        'aggressive': { title: "Ultimatum Failed", script: "You threatened to quit. They called your bluff. Never issue an ultimatum unless you actually have another job offer in hand." },
        'stalled': { title: "Hit a Brick Wall", script: "You kept demanding cash when they repeatedly told you the cash budget was locked. You need to learn how to pivot to 'Total Compensation'." }
    }
  },

  // --- NEW: THE EXPLODING OFFER ---
  'exploding-offer': {
    id: 'game_negotiation_exploding',
    moduleId: 'negotiation',
    title: 'The Exploding Offer',
    targetSalary: 75000,
    difficultyLevels: {
        rookie: { label: "TRAINING", opponentType: 'standard_hr', offerGap: 5000, turnLimit: 6, initialMessage: "We're offering $70k. We need an answer by tomorrow morning." },
        standard: { label: "STANDARD", opponentType: 'aggressive_founder', offerGap: 10000, turnLimit: 5, initialMessage: "We're offering $65k. We need an answer by 5 PM today." },
        expert: { label: "EXPERT (The Shark)", opponentType: 'aggressive_founder', offerGap: 15000, turnLimit: 4, initialMessage: "We're offering $60k. This is an exploding offer. If I don't hear a 'Yes' by 5 PM today, we're calling the next candidate." }
    },
    preGameBriefing: {
        title: "Defusing the Bomb",
        script: "An 'Exploding Offer' is a bullying tactic designed to prevent you from interviewing elsewhere or thinking clearly. Your Mission: Secure $75,000 AND buy yourself 48 hours to review the contract.",
        videoUrl: null
    },
    failureLogic: {
        'accepted_too_fast': { title: "Caved to Pressure", script: "You caved. By accepting instantly, you left money on the table and signaled that their bullying tactics work." },
        'stalled': { title: "Offer Revoked", script: "You failed to build rapport while delaying, and they moved on to the next candidate." }
    }
  }
};